<template>
  <div class="pa-2 fleet-charging-panel">
    <v-card flat class="mb-4">
      <v-card-title>
        <span class="headline">Fleet Charging</span>
        <v-spacer />
        <v-btn v-if="canManageCharging" color="primary" @click="openAssignVehicleDialog">
          Assign vehicle
        </v-btn>
      </v-card-title>
      <v-card-text v-if="!canManageCharging">
        <v-alert v-if="!canManageCharging" type="info" :value="true" class="mb-3">
          You have read-only access to fleet charging assignments.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-tabs v-model="activeFleetSubTab" class="mb-3" @change="onFleetSubTabChanged">
      <v-tabs-slider color="primary" />
      <v-tab ripple>Charging Sessions</v-tab>
      <v-tab ripple>Current Assignments</v-tab>
    </v-tabs>

    <div v-show="activeFleetSubTab === 0">
      <v-card flat>
        <v-card-text>
          <v-layout row wrap align-center>
            <v-flex xs12 sm3 pa-2>
              <v-text-field v-model="filters.chargePointId" label="Charge Point ID" clearable hide-details @keyup.enter.native="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm3 pa-2>
              <v-text-field v-model="filters.idTag" label="idTag" clearable hide-details @keyup.enter.native="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pa-2>
              <st-date-field id="fleet-charging-date-from" v-model="filters.dateFrom" label="From" hide-details to-date-id="fleet-charging-date-to" @change="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pl-4 pa-2>
              <st-date-field id="fleet-charging-date-to" v-model="filters.dateTo" label="To" hide-details from-date-id="fleet-charging-date-from" @change="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pa-2 class="text-xs-right">
              <v-btn fab small :loading="isLoading" title="Refresh sessions" @click="refreshQueue">
                <v-icon>fal fa-filter</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-data-table
          :headers="headers"
          :items="sessions"
          :loading="isLoading"
          :pagination.sync="sessionsPagination"
          :custom-sort="passthroughSort"
          :rows-per-page-items="[10, 25, 50]"
        >
          <template v-slot:items="{ item }">
            <tr>
              <td>{{ item.charge_point_id || '-' }}</td>
              <td>{{ item.id_tag || '-' }}</td>
              <td>{{ item.status || '-' }}</td>
              <td>{{ item.verification_status || '-' }}</td>
              <td>{{ item.license_plate_number || '-' }}</td>
              <td>{{ formatDateTime(item.start_time) }}</td>
              <td>{{ formatKwh(item.energy_delivered_kwh) }}</td>
              <td class="text-xs-right">
                <v-btn v-if="canManageCharging" small @click="openSessionDialog(item)">
                  {{ item.transportation_profile_id ? 'Manage' : 'Assign Vehicle' }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <div v-show="activeFleetSubTab === 1">
      <v-card flat class="mb-4">
        <v-card-title>
          <span class="title">Current Assignments</span>
          <v-spacer />
          <v-btn small flat :loading="activeAssignmentsLoading" @click="refreshActiveAssignments">Refresh</v-btn>
        </v-card-title>
        <v-data-table
          :headers="activeAssignmentHeaders"
          :items="activeAssignments"
          :loading="activeAssignmentsLoading"
          :rows-per-page-items="[10, 25, 50]"
        >
          <template v-slot:items="{ item }">
            <tr>
              <td>{{ item.charge_point_id || '-' }}</td>
              <td>{{ item.location_label || '-' }}</td>
              <td>{{ item.license_plate_number || '-' }}</td>
              <td>{{ item.status || '-' }}</td>
              <td>{{ formatDateTime(item.date_created) }}</td>
              <td class="text-xs-right">
                <v-btn
                  v-if="canManageCharging"
                  small
                  color="warning"
                  :loading="actionInProgress"
                  @click="releaseAssignment(item)"
                >
                  Release
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="assignDialogVisible" max-width="1100" @input="onAssignDialogInput">
      <v-card>
        <v-card-title>
          <span class="headline">Assign Vehicle to Charger</span>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md5 pa-2>
              <transportation-profile-autocomplete
                v-model="selectedProfile"
                label="Vehicle"
                placeholder="Search by plate, shortcode, or driver"
                :show-owner-account="true"
                :show-driver="true"
                :disabled="!canManageCharging || actionInProgress"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 md7 pa-2>
              <st-map
                v-if="stMapApis && assignDialogVisible"
                disable-search
                disable-map-hash
                container-id="fleetChargingPortalMap"
                height="360px"
                :mapbox-access-token="mapboxAccessToken"
                :maptiler-key="maptilerKey"
                :api="stMapApis"
                :on-map-loaded="onMapLoaded"
              />
            </v-flex>
            <v-flex xs12 md5 pa-2>
              <v-text-field
                v-model="chargerSearch"
                label="Charger name or location"
                append-icon="fal fa-search"
                clearable
                :loading="searchInProgress"
                :disabled="searchInProgress"
                @keyup.enter.native="searchChargers"
                @click:append="searchChargers"
              />
              <v-list two-line class="fleet-charging-panel__charger-list">
                <v-list-tile
                  v-for="charger in chargers"
                  :key="charger.id"
                  :class="{ 'primary--text': selectedCharger && selectedCharger.id === charger.id }"
                  @click="selectCharger(charger)"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{ charger.chargePointId }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ charger.locationLabel || 'No location label' }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-chip small :color="isChargerInUse(charger) ? 'warning' : 'success'" text-color="white">
                      {{ isChargerInUse(charger) ? 'In use' : 'Free' }}
                    </v-chip>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn flat color="warning" @click="assignDialogVisible = false">Cancel</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="actionInProgress"
            :disabled="!canAssignSelectedCharger"
            @click="assignSelectedCharger"
          >
            Assign Vehicle to Charger
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogVisible" max-width="920">
      <v-card>
        <v-card-title><span class="headline">Session Assignment</span></v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md6>
              <div><strong>Charge Point:</strong> {{ selectedSession && selectedSession.charge_point_id ? selectedSession.charge_point_id : '-' }}</div>
              <div><strong>Transaction:</strong> {{ selectedSession && selectedSession.ocpp_transaction_id ? selectedSession.ocpp_transaction_id : '-' }}</div>
              <div><strong>idTag:</strong> {{ selectedSession && selectedSession.id_tag ? selectedSession.id_tag : '-' }}</div>
              <div><strong>Status:</strong> {{ selectedSession && selectedSession.status ? selectedSession.status : '-' }}</div>
            </v-flex>
            <v-flex xs12 md6>
              <div><strong>Verification:</strong> {{ selectedSession && selectedSession.verification_status ? selectedSession.verification_status : '-' }}</div>
              <div><strong>Start:</strong> {{ selectedSession ? formatDateTime(selectedSession.start_time) : '-' }}</div>
              <div><strong>Stop:</strong> {{ selectedSession ? formatDateTime(selectedSession.stop_time) : '-' }}</div>
              <div><strong>Energy:</strong> {{ selectedSession ? formatKwh(selectedSession.energy_delivered_kwh) : '-' }}</div>
            </v-flex>
          </v-layout>
          <v-divider class="my-4" />
          <v-alert v-if="selectedSession && !selectedSessionIsActive" type="info" :value="true" class="mb-3">
            This charging session is complete. You can still assign a vehicle for audit history, but provisioning actions require an active charging session.
          </v-alert>
          <transportation-profile-autocomplete
            v-model="assignment.transportationProfile"
            label="Vehicle to assign"
            placeholder="Search by plate, shortcode, or driver"
            :show-owner-account="true"
            :show-driver="true"
            :disabled="!canManageCharging"
          />
          <v-layout row wrap>
            <v-flex xs12 sm6 pa-1>
              <v-select v-model="assignment.vehicleClass" :items="vehicleClasses" item-text="text" item-value="value" label="Vehicle class for idTag" />
            </v-flex>
            <v-flex xs12 sm6 pa-1 class="d-flex align-center">
              <v-checkbox v-model="assignment.applyToIdTag" label="Apply assignment to idTag for future sessions" hide-details />
            </v-flex>
            <v-flex xs12>
              <v-textarea v-model="assignment.notes" rows="2" auto-grow label="Assignment notes (optional)" />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="primary" :loading="actionInProgress" :disabled="!canManageCharging || actionInProgress" @click="onAssignSession">
                Assign Vehicle
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="warning" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onStartProvisioning">
                Start Provisioning
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="success" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onFinalizeProvisioning">
                Finalize Provisioning
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mt-1">
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="secondary" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onReProvision">
                Re-Provision
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat color="warning" @click="dialogVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatDate, formatTime } from '@smarttransit/common'
import { addAlert, isAuthorized } from '@/utilities/helpers'
import { genericApiRequests } from '@/utilities/axios-factory'
import { findOcppChargers } from '@/services/ocpp-charger-service'
import {
  getChargingAssignmentQueue,
  assignChargingSession,
  startProvisioning,
  finalizeProvisioning,
  reProvision,
  assignVehicleToCharger,
  releaseVehicleFromCharger,
  getActiveChargerAssignments
} from '@/services/ocpp-assignment-service'

const MAP_SOURCE_ID = 'fleetChargingAssignmentSource'
const MAP_LAYER_ID = 'fleetChargingAssignmentLayer'
const MAP_HIT_LAYER_ID = 'fleetChargingAssignmentHitLayer'
const MAP_IMAGE_ID = 'ev-charger-marker'
const MAP_MARKER_ICON_SIZE = 0.28

function mapPopupHtml (charger) {
  return `
    <h4>${charger.chargePointId || '-'}</h4>
    <div>Status: ${charger.status || '-'}</div>
    <div>Model: ${charger.model || '-'}</div>
    <div><small>${charger.locationLabel || '-'}</small></div>
  `
}

export default {
  name: 'fleet-charging-panel',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      activeFleetSubTab: 0,
      assignDialogVisible: false,
      selectedProfile: null,
      selectedCharger: null,
      chargerSearch: '',
      chargers: [],
      stMapApis: null,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      searchInProgress: false,
      activeAssignmentsLoading: false,
      activeAssignments: [],
      sessions: [],
      mapboxInstanceWrapper: null,
      mapMarkerEventsRegistered: false,
      mapPopup: null,
      isLoading: false,
      actionInProgress: false,
      dialogVisible: false,
      selectedSession: null,
      filters: {
        chargePointId: '',
        idTag: '',
        dateFrom: '',
        dateTo: ''
      },
      sessionsPagination: {
        sortBy: null,
        descending: false,
        page: 1,
        rowsPerPage: 10
      },
      assignment: {
        transportationProfile: null,
        vehicleClass: 'fleet_bus',
        applyToIdTag: true,
        notes: ''
      },
      vehicleClasses: [
        { text: 'Fleet bus', value: 'fleet_bus' },
        { text: 'Private vehicle', value: 'private_vehicle' }
      ],
      headers: [
        { text: 'Charge Point', value: 'charge_point_id', sortable: true },
        { text: 'idTag', value: 'id_tag', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Verification', value: 'verification_status', sortable: true },
        { text: 'Vehicle', value: 'license_plate_number', sortable: true },
        { text: 'Start', value: 'start_time', sortable: true },
        { text: 'kWh', value: 'energy_delivered_kwh', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],
      activeAssignmentHeaders: [
        { text: 'Charge Point', value: 'charge_point_id', sortable: true },
        { text: 'Location', value: 'location_label', sortable: true },
        { text: 'Vehicle', value: 'license_plate_number', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Assigned', value: 'date_created', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    canManageCharging () {
      return isAuthorized(this.signedInUser, 'agentdriver')
    },
    canAssignSelectedCharger () {
      return this.canManageCharging && this.selectedProfile && this.selectedProfile.id && this.selectedCharger && !this.isChargerInUse(this.selectedCharger) && !this.actionInProgress
    },
    selectedSessionIsActive () {
      const status = this.selectedSession && this.selectedSession.status ? String(this.selectedSession.status).toLowerCase() : ''
      return status === 'inprogress' && !(this.selectedSession && this.selectedSession.stop_time)
    },
    canRunSelectedSessionIdTagAction () {
      return this.canManageCharging && !this.actionInProgress && this.selectedSessionIsActive && !!(this.selectedSession && this.selectedSession.id_tag)
    },
    sessionsOrder () {
      const { sortBy, descending } = this.sessionsPagination
      if (!sortBy || descending === null) {
        return null
      }
      return `${sortBy} ${descending ? 'DESC' : 'ASC'}`
    }
  },
  watch: {
    sessionsOrder () {
      this.refreshQueue()
    }
  },
  mounted () {
    this.stMapApis = {
      genericApiRequests,
      addAlert
    }
    this.searchChargers()
    this.refreshActiveAssignments()
    this.refreshQueue()
  },
  methods: {
    formatDateTime (value) {
      if (!value) {
        return '-'
      }
      return `${formatTime(value)}, ${formatDate(value)}`
    },
    formatKwh (value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return `${value} kWh`
    },
    isChargerInUse (charger) {
      return !!(charger && charger.availability && charger.availability.inUse)
    },
    selectCharger (charger) {
      this.selectedCharger = charger
    },
    focusPanel () {
      this.refreshActiveFleetSubTab()
      this.focusMap()
    },
    refreshActiveFleetSubTab () {
      if (Number(this.activeFleetSubTab) === 0) {
        return this.refreshQueue()
      }
      return this.refreshActiveAssignments()
    },
    onFleetSubTabChanged (value) {
      this.activeFleetSubTab = Number(value)
      return this.refreshActiveFleetSubTab()
    },
    openAssignVehicleDialog () {
      this.assignDialogVisible = true
      this.searchChargers()
      this.$nextTick(() => {
        this.focusMap()
      })
    },
    onAssignDialogInput (value) {
      if (value) {
        this.focusMap()
        return
      }
      this.closeMapPopup()
    },
    onMapLoaded (mapboxInstance) {
      this.mapboxInstanceWrapper = Object.freeze({ mapboxInstance })
      this.mapMarkerEventsRegistered = false
      this.closeMapPopup()
      return this.focusMap()
    },
    getMapboxInstance () {
      return this.mapboxInstanceWrapper ? this.mapboxInstanceWrapper.mapboxInstance : null
    },
    focusMap () {
      this.$nextTick(() => {
        setTimeout(() => {
          const mapboxInstance = this.getMapboxInstance()
          window.dispatchEvent(new Event('resize'))
          if (mapboxInstance && mapboxInstance.currentMap && typeof mapboxInstance.currentMap.resize === 'function') {
            mapboxInstance.currentMap.resize()
          }
          this.renderMapMarkers()
        }, 100)
      })
    },
    closeMapPopup () {
      if (this.mapPopup) {
        this.mapPopup.remove()
        this.mapPopup = null
      }
    },
    showMapPopup (feature, options) {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap || !feature || !feature.properties || !feature.geometry) {
        return
      }
      options = options || {}

      this.closeMapPopup()
      this.mapPopup = mapboxInstance.createPopup({
        className: 'st-map--popup',
        closeButton: !!options.closeButton,
        closeOnClick: !!options.closeOnClick,
        offset: 12
      })
      this.mapPopup.on('close', () => {
        this.mapPopup = null
      })
      this.mapPopup
        .setLngLat(feature.geometry.coordinates.slice())
        .setHTML(mapPopupHtml(feature.properties))
        .addTo(mapboxInstance.currentMap)
    },
    cloneMapFeature (feature) {
      if (!feature) {
        return null
      }
      return JSON.parse(JSON.stringify({
        geometry: {
          type: feature.geometry && feature.geometry.type,
          coordinates: feature.geometry && feature.geometry.coordinates ? feature.geometry.coordinates.slice() : []
        },
        properties: Object.assign({}, feature.properties || {})
      }))
    },
    async renderMapMarkers () {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap) {
        return
      }
      const currentMap = mapboxInstance.currentMap
      const features = (this.chargers || [])
        .filter((charger) => typeof charger.longitude === 'number' && typeof charger.latitude === 'number')
        .map((charger) => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [charger.longitude, charger.latitude]
            },
            properties: {
              id: charger.id,
              chargePointId: charger.chargePointId || '',
              status: charger.status || '',
              vendor: charger.vendor || '',
              model: charger.model || '',
              locationLabel: charger.locationLabel || '',
              inUse: this.isChargerInUse(charger),
              longitude: charger.longitude,
              latitude: charger.latitude,
              lng: charger.longitude,
              lat: charger.latitude
            }
          }
        })

      const sourceData = {
        type: 'FeatureCollection',
        features
      }

      if (currentMap.getSource(MAP_SOURCE_ID)) {
        currentMap.getSource(MAP_SOURCE_ID).setData(sourceData)
      } else {
        currentMap.addSource(MAP_SOURCE_ID, {
          type: 'geojson',
          data: sourceData
        })
      }

      if (!currentMap.hasImage(MAP_IMAGE_ID)) {
        await mapboxInstance.addLayerImage(`${location.origin}/images/ev-charger-marker.png`, MAP_IMAGE_ID)
      }

      if (!currentMap.getLayer(MAP_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_LAYER_ID,
          type: 'symbol',
          source: MAP_SOURCE_ID,
          layout: {
            'icon-image': MAP_IMAGE_ID,
            'icon-size': MAP_MARKER_ICON_SIZE,
            'icon-anchor': 'center',
            'icon-allow-overlap': true
          }
        })
      } else {
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-size', MAP_MARKER_ICON_SIZE)
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-anchor', 'center')
      }

      if (!currentMap.getLayer(MAP_HIT_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_HIT_LAYER_ID,
          type: 'circle',
          source: MAP_SOURCE_ID,
          paint: {
            'circle-radius': 22,
            'circle-color': '#204da2',
            'circle-opacity': 0.01
          }
        })
      }

      this.registerMapMarkerEvents()
    },
    registerMapMarkerEvents () {
      const mapboxInstance = this.getMapboxInstance()
      if (this.mapMarkerEventsRegistered || !mapboxInstance || !mapboxInstance.currentMap) {
        return
      }

      const currentMap = mapboxInstance.currentMap

      currentMap.on('click', MAP_HIT_LAYER_ID, (event) => {
        const feature = this.cloneMapFeature(event && event.features && event.features.length ? event.features[0] : null)
        if (!feature || !feature.properties || !feature.properties.id) {
          return
        }
        const charger = this.chargers.find((item) => item.id === feature.properties.id)
        if (charger) {
          this.selectCharger(charger)
        }
      })

      currentMap.on('mouseenter', MAP_HIT_LAYER_ID, (event) => {
        const feature = this.cloneMapFeature(event && event.features && event.features.length ? event.features[0] : null)
        if (!feature || !feature.properties || !feature.properties.id) {
          return
        }

        currentMap.getCanvas().style.cursor = 'pointer'
        this.showMapPopup(feature, { closeButton: false, closeOnClick: false })
      })

      currentMap.on('mouseleave', MAP_HIT_LAYER_ID, () => {
        currentMap.getCanvas().style.cursor = ''
        this.closeMapPopup()
      })

      this.mapMarkerEventsRegistered = true
    },
    async searchChargers () {
      this.searchInProgress = true
      try {
        this.chargers = await findOcppChargers({
          limit: 50,
          offset: 0,
          order: 'dateUpdated DESC',
          search: this.chargerSearch || undefined
        })
        if (this.selectedCharger) {
          const updatedSelected = this.chargers.find((charger) => charger.id === this.selectedCharger.id)
          this.selectedCharger = updatedSelected || null
        }
        this.$nextTick(() => {
          this.focusMap()
        })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.searchInProgress = false
      }
    },
    async assignSelectedCharger () {
      if (!this.canAssignSelectedCharger) {
        return
      }
      this.actionInProgress = true
      try {
        await assignVehicleToCharger({
          chargePointId: this.selectedCharger.chargePointId,
          transportationProfileId: this.selectedProfile.id
        })
        addAlert({ message: 'Vehicle assigned to charger', type: 'success' })
        this.assignDialogVisible = false
        await Promise.all([this.searchChargers(), this.refreshActiveAssignments()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async refreshActiveAssignments () {
      this.activeAssignmentsLoading = true
      try {
        this.activeAssignments = await getActiveChargerAssignments()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.activeAssignmentsLoading = false
      }
    },
    async releaseAssignment (assignment) {
      if (!assignment || !assignment.charge_point_id) {
        return
      }
      const chargerLabel = assignment.charge_point_id
      const vehicleLabel = assignment.license_plate_number ? ' for ' + assignment.license_plate_number : ''
      if (!confirm('Confirm releasing the charger assignment' + vehicleLabel + ' from ' + chargerLabel + '?')) {
        return
      }
      this.actionInProgress = true
      try {
        await releaseVehicleFromCharger({ chargePointId: assignment.charge_point_id })
        addAlert({ message: 'Assignment released', type: 'success' })
        await Promise.all([this.searchChargers(), this.refreshActiveAssignments()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    // Sorting is delegated to the API via sessionsOrder, so keep the rows as returned
    passthroughSort (items) {
      return items
    },
    async refreshQueue () {
      this.isLoading = true
      try {
        this.sessions = await getChargingAssignmentQueue({
          chargePointId: this.filters.chargePointId || undefined,
          idTag: this.filters.idTag || undefined,
          dateFrom: this.filters.dateFrom || undefined,
          dateTo: this.filters.dateTo || undefined,
          order: this.sessionsOrder || undefined,
          limit: 100
        })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.isLoading = false
      }
    },
    openSessionDialog (session) {
      this.selectedSession = Object.assign({}, session)
      this.assignment.transportationProfile = session && session.transportation_profile_id
        ? {
          id: session.transportation_profile_id,
          licensePlateNumber: session.license_plate_number || 'Assigned vehicle'
        }
        : null
      this.assignment.vehicleClass = session && session.vehicle_class ? session.vehicle_class : 'fleet_bus'
      this.assignment.applyToIdTag = true
      this.assignment.notes = ''
      this.dialogVisible = true
    },
    async onAssignSession () {
      if (!this.selectedSession || !this.selectedSession.ocpp_transaction_id) {
        return
      }
      if (!this.assignment.transportationProfile || !this.assignment.transportationProfile.id) {
        addAlert({ message: 'Please select a vehicle profile first', type: 'error', isModal: true })
        return
      }

      this.actionInProgress = true
      try {
        await assignChargingSession({
          transactionId: this.selectedSession.ocpp_transaction_id,
          transportationProfileId: this.assignment.transportationProfile.id,
          idTag: this.selectedSession.id_tag,
          vehicleClass: this.assignment.vehicleClass,
          applyToIdTag: this.assignment.applyToIdTag,
          notes: this.assignment.notes
        })
        addAlert({ message: 'Charging session assignment updated', type: 'success' })
        await this.refreshQueue()
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async onStartProvisioning () {
      await this.runIdTagAction(startProvisioning, 'Provisioning started')
    },
    async onFinalizeProvisioning () {
      await this.runIdTagAction(finalizeProvisioning, 'Provisioning finalized')
    },
    async onReProvision () {
      await this.runIdTagAction(reProvision, 'Re-provisioning started')
    },
    async runIdTagAction (handler, successMessage) {
      if (!this.canRunSelectedSessionIdTagAction) {
        return
      }
      this.actionInProgress = true
      try {
        await handler({ idTag: this.selectedSession.id_tag })
        addAlert({ message: successMessage, type: 'success' })
        await this.refreshQueue()
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    }
  },
  beforeDestroy () {
    this.closeMapPopup()
    this.mapMarkerEventsRegistered = false
  }
}
</script>

<style scoped>
.fleet-charging-panel__charger-list {
  max-height: 360px;
  overflow-y: auto;
}
</style>
